import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { View, StyleSheet, Alert, Dimensions, Platform, TouchableOpacity } from 'react-native';
import { Modal, Portal } from 'react-native-paper';
import { WebView } from 'react-native-webview';
import {Feather, MaterialCommunityIcons} from '@expo/vector-icons';
import Button from "../../Component/Button";
import { CareshipColors } from "../../Component/CSTheme";
import { FormattedMessage } from "react-intl";
import {Container} from "../../Component";
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator'; // For rotating images

const SignatureScreen = forwardRef((props, ref) => {
  const webviewRef = useRef(null);
  const webviewRef2 = useRef(null);
  const canvasRef = useRef(null);
  const [signatureData, setSignatureData] = useState(null);
  const { width, height } = Dimensions.get('window');
  const [isEmpty, setIsEmpty] = useState(true); // Track if the canvas is empty
  const [isFullScreen, setIsFullScreen] = useState(false); // Track if canvas is in full-screen mode
  const [modalVisible, setModalVisible] = useState(false); // Track modal visibility

  const containerStyle = {  backgroundColor: 'white', width: '100%', height: '100%', position: 'fixed'};
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getSignature() {
      if (Platform.OS === 'web') {
        getSignatureFromCanvas();
      } else if (webviewRef.current) {
        webviewRef.current.injectJavaScript('getSignature();');
      }
      else if (webviewRef2.current) {
        webviewRef2.current.injectJavaScript('getSignatureFullScreen();');
      }
    }
  }));

  const handleClear = () => {
    if (Platform.OS === 'web') {
      clearSignatureCanvas();
    } else if (webviewRef.current) {
      webviewRef.current.injectJavaScript('clearSignature();');
    }
    setSignatureData(null);
  };

  const handleClearFullScreen = () => {
    if (webviewRef2.current) {
      webviewRef2.current.injectJavaScript('clearSignatureFullScreen();');
      setSignatureData(null);
    }

  };

  const handleMessage = (event) => {
    const { data } = event.nativeEvent;
    if (data.startsWith('data:image')) {
      if (props.onSignatureReceived) {
        props.onSignatureReceived(event.nativeEvent.data);
      }
    } else {
      Alert.alert('Signature', 'No signature entered');
    }
  };
  const handleMessageRotated = async(event) => {
    const { data } = event.nativeEvent;
    if (data.startsWith('data:image')) {

      const rotatedImage = await rotateImage(data);
      setSignatureData(rotatedImage);
      if (props.onSignatureReceived) {
        props.onSignatureReceived(rotatedImage);
      }
    } else {
      Alert.alert('Signature', 'No signature entered');
    }
  };

  const rotateImage = async (base64) => {
    try {
      const result = await manipulateAsync(
        base64,
        [{ rotate: 270 }],
        { format: SaveFormat.PNG, base64: true, width: 300 }
      );
      return 'data:image/png;base64,'+result.base64; // Return rotated image
    } catch (error) {
      console.error('Error rotating image:', error);
      return base64; // Return original in case of error
    }
  };
  const getSignatureFromCanvas = () => {
    const canvas = canvasRef.current;
    if (!isEmpty && canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      setSignatureData(dataUrl);
      if (props.onSignatureReceived) {
        props.onSignatureReceived(dataUrl);
      }
    } else {
      Alert.alert('Signature', 'No signature entered');
    }
  };

  const clearSignatureCanvas = () => {
    setIsEmpty(true)
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    setModalVisible(!modalVisible); // Open the modal when full-screen is toggled
  };

    const handleDone = () => {
      if (webviewRef2.current) {
        webviewRef2.current.injectJavaScript('getSignatureFullScreen();');
        setIsFullScreen(false);
        setModalVisible(false); // Open the modal when full-screen is toggled
      }
    }

  const signaturePadHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body, html { margin: auto; padding: 0; width: calc(100vw - 120px); height: 200px; display: flex; justify-content: center; align-items: center; }
        canvas { border: 1px solid black; margin: auto; touch-action: none; width: calc(100vw - 120px); height: 200px; }
      </style>
    </head>
    <body>
      <canvas id="signature-pad"></canvas>
      <script>
        (function() {
          const canvas = document.getElementById('signature-pad');
          const ctx = canvas.getContext('2d');
          let drawing = false;
          let previous = null;
          let emptyCanvas = true;

          function resizeCanvas() {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            ctx.scale(ratio, ratio);
          }

          function getPosition(event) {
            const rect = canvas.getBoundingClientRect();
            return {
              x: (event.clientX - rect.left) * (canvas.width / rect.width),
              y: (event.clientY - rect.top) * (canvas.height / rect.height)
            };
          }

          function startDrawing(event) {
            ctx.imageSmoothingEnabled = true;
            ctx.lineCap = "round";
            ctx.lineWidth = 1 * Math.max(window.devicePixelRatio, 1, 1)
            drawing = true;
            previous = getPosition(event);
            emptyCanvas = false;
          }

          function draw(event) {
            if (!drawing) return;
            const current = getPosition(event);
            ctx.beginPath();
            ctx.moveTo(previous.x, previous.y);
            ctx.lineTo(current.x, current.y);
            ctx.stroke();
            previous = current;
          }

          function stopDrawing() {
            drawing = false;
            previous = null;
          }

          function clearSignature() {
              emptyCanvas = true;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }

          function getSignature() {
            const dataUrl = emptyCanvas?  '-': canvas.toDataURL('image/png');
            window.ReactNativeWebView.postMessage(dataUrl);
          }

          window.clearSignature = clearSignature;
          window.getSignature = getSignature;

          canvas.removeEventListener('mousedown', startDrawing);
          canvas.removeEventListener('mousemove', draw);
          canvas.removeEventListener('mouseup', stopDrawing);
          canvas.removeEventListener('mouseleave', stopDrawing);

          canvas.addEventListener('mousedown', startDrawing);
          canvas.addEventListener('mousemove', draw);
          canvas.addEventListener('mouseup', stopDrawing);
          canvas.addEventListener('mouseleave', stopDrawing);

          canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            startDrawing(e.touches[0]);
          });
          canvas.addEventListener('touchmove', (e) => {
            e.preventDefault();
            draw(e.touches[0]);
          });
          canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            stopDrawing();
          });
          canvas.addEventListener('touchcancel', (e) => {
            e.preventDefault();
            stopDrawing();
          });
        })();
      </script>
    </body>
    </html>
  `;

  const signaturePadHtmlFullScreen = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body, html { margin: auto; padding: 0; width:100%; height: 100%; display: flex; justify-content: center; align-items: center; }
        canvas { border: 1px solid black; margin: auto; touch-action: none; width:100%; height: 100%; }
      </style>
    </head>
    <body>
      <canvas id="signature-pad-full-screen"></canvas>
      <script>
        (function() {
          const canvasFullScreen = document.getElementById('signature-pad-full-screen');
          const ctxFullScreen = canvasFullScreen.getContext('2d');
          let drawingFullScreen = false;
          let previousFullScreen = null;
          let emptyCanvasFullScreen = true;

          function resizeCanvasFullScreen() {
            //const ratioFullScreen = Math.max(window.devicePixelRatio, 1, 1);
            canvasFullScreen.width =  700;
            canvasFullScreen.height = 1500;
          }

          function getPositionFullScreen(event) {
            const rectFullScreen = canvasFullScreen.getBoundingClientRect();
            return {
              x: (event.clientX - rectFullScreen.left) * (canvasFullScreen.width / rectFullScreen.width),
              y: (event.clientY - rectFullScreen.top) * (canvasFullScreen.height / rectFullScreen.height)
            };
          }

          function startDrawingFullScreen(event) {
            ctxFullScreen.imageSmoothingEnabled = true;
            ctxFullScreen.lineCap = "round";
            ctxFullScreen.lineWidth = 10 * Math.max(window.devicePixelRatio, 1, 1)
            drawingFullScreen = true;
            previousFullScreen = getPositionFullScreen(event);
            emptyCanvasFullScreen = false;

          }

          function drawFullScreen(event) {
            if (!drawingFullScreen) return;
            const currentFullScreen = getPositionFullScreen(event);
            ctxFullScreen.beginPath();
            ctxFullScreen.moveTo(previousFullScreen.x, previousFullScreen.y);
            ctxFullScreen.lineTo(currentFullScreen.x, currentFullScreen.y);
            ctxFullScreen.stroke();
            previousFullScreen = currentFullScreen;
          }

          function stopDrawingFullScreen() {
            drawingFullScreen = false;
            previousFullScreen = null;
          }

          function clearSignatureFullScreen() {
              emptyCanvasFullScreen = true;
            ctxFullScreen.clearRect(0, 0, canvasFullScreen.width, canvasFullScreen.height);
          }

          function getSignatureFullScreen() {
            const dataUrlFullScreen = emptyCanvasFullScreen?  '-': canvasFullScreen.toDataURL('image/png');
            window.ReactNativeWebView.postMessage(dataUrlFullScreen);
          }

          window.clearSignatureFullScreen = clearSignatureFullScreen;
          window.getSignatureFullScreen = getSignatureFullScreen;

          canvasFullScreen.removeEventListener('mousedown', startDrawingFullScreen);
          canvasFullScreen.removeEventListener('mousemove', drawFullScreen);
          canvasFullScreen.removeEventListener('mouseup', stopDrawingFullScreen);
          canvasFullScreen.removeEventListener('mouseleave', stopDrawingFullScreen);

          canvasFullScreen.addEventListener('mousedown', startDrawingFullScreen);
          canvasFullScreen.addEventListener('mousemove', drawFullScreen);
          canvasFullScreen.addEventListener('mouseup', stopDrawingFullScreen);
          canvasFullScreen.addEventListener('mouseleave', stopDrawingFullScreen);

          resizeCanvasFullScreen();

          canvasFullScreen.addEventListener('touchstart', (e) => {
            e.preventDefault();
            startDrawingFullScreen(e.touches[0]);
          });
          canvasFullScreen.addEventListener('touchmove', (e) => {
            e.preventDefault();
            drawFullScreen(e.touches[0]);
          });
          canvasFullScreen.addEventListener('touchend', (e) => {
            e.preventDefault();
            stopDrawingFullScreen();
          });
          canvasFullScreen.addEventListener('touchcancel', (e) => {
            e.preventDefault();
            stopDrawingFullScreen();
          });

        })();
      </script>
    </body>
    </html>
  `;

    useEffect(() => {
      if (Platform.OS === 'web' && canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let drawing = false;
        let previous = null;


        const resizeCanvas = () => {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          ctx.scale(ratio, ratio);
        };

        const getPosition = (event) => {
          const rect = canvas.getBoundingClientRect(); // Get the canvas' bounding box
          const scrollX = window.scrollX || window.pageXOffset;
          const scrollY = window.scrollY || window.pageYOffset;

          // Support both touch and mouse events
          const clientX = event.touches ? event.touches[0].clientX : event.clientX;
          const clientY = event.touches ? event.touches[0].clientY : event.clientY;

          return {
            x: (clientX - rect.left + scrollX) * (canvas.width / rect.width),
            y: (clientY - rect.top + scrollY) * (canvas.height / rect.height),
          };
        };

        const startDrawing = (event) => {
          drawing = true;
          setIsEmpty(false)
          previous = getPosition(event);
        };

        const draw = (event) => {
          if (!drawing) return;
          const current = getPosition(event);
          ctx.beginPath();
          ctx.moveTo(previous.x, previous.y);
          ctx.lineTo(current.x, current.y);
          ctx.stroke();
          previous = current;
        };

        const stopDrawing = () => {
          drawing = false;
          previous = null;
        };

        //resizeCanvas();
        //window.addEventListener('resize', resizeCanvas);

        canvas.addEventListener('mousedown', startDrawing);
        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', stopDrawing);
        canvas.addEventListener('mouseleave', stopDrawing);

        // Event listeners for touch events
        canvas.addEventListener('touchstart', startDrawing, { passive: false });
        canvas.addEventListener('touchmove', draw, { passive: false });
        canvas.addEventListener('touchend', stopDrawing);

        return () => {
          // Cleanup mouse events
          canvas.removeEventListener('mousedown', startDrawing);
          canvas.removeEventListener('mousemove', draw);
          canvas.removeEventListener('mouseup', stopDrawing);
          canvas.removeEventListener('mouseleave', stopDrawing);

          // Cleanup touch events
          canvas.removeEventListener('touchstart', startDrawing);
          canvas.removeEventListener('touchmove', draw);
          canvas.removeEventListener('touchend', stopDrawing);
        };
      }
    }, []);

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' ? (
        <canvas
          ref={canvasRef}
          style={{
            border: '1px solid black',
            width: `300px`,
            height: '100px',
            marginBottom: '10px',
          }}
        />
      ) : (
        <>
          <View style={styles.buttonsSpecial}>
            <Button
              style={styles.specialButtonsNormal}
              onPress={toggleFullScreen}
              size={'xs'}
              contentStyle={{width:0, padding:0}}
              icon={<MaterialCommunityIcons size={16} color={CareshipColors.secondary} name="arrow-expand" style={{margin:0,padding: 5, textAlign: 'center', lineHeight: 20, backgroundColor: 'white', borderColor: CareshipColors.secondary, borderWidth: 1, borderRadius: 50, width: 32, height: 32}}/>}
            ></Button>
          </View>
          <WebView
            ref={webviewRef}
            originWhitelist={['*']}
            source={{ html: signaturePadHtml }}
            javaScriptEnabled={true}
            onMessage={handleMessage}
            style={[styles.webview, { width: width - 50, height: 200 }]}
          />

        </>
      )}
      <>
        <View style={styles.buttons}>
          <Button
            style={styles.redoButton}
            onPress={handleClear}
            size="sm"
            labelStyle={{color: CareshipColors.secondary }}
            icon={<Feather color={CareshipColors.secondary} size={16} name="refresh-cw" />}
          >
            <FormattedMessage id="BUTTON_UNDO_SIGNATURE_SCREEN" />
          </Button>

        </View>
      </>

      {/* Full Screen Modal */}
      <Portal>
        <Modal
          animationType="slide"
          visible={modalVisible}
          dismissable={false}
        >
          <View style={{ ...styles.modal, width: Platform.OS === 'web' ? 350 : width - 20 }}>
            <TouchableOpacity
              style={{
                position: 'absolute',
                right: 5,
                zIndex: 1, // Ensure the button is above the content
                bottom:5
              }}
              onPress={() => toggleFullScreen()}
            >
              <Feather name="x" size={24} color={CareshipColors.red} />
            </TouchableOpacity>
            <View style={{ ...styles.modalContent, width: '100%', height: '100%'}}>
              <View style={{ ...styles.groupFullScreen, width: '100%', height: '100%'}}>

                <Container style={{...styles.buttonsFullScreen, marginLeft: 0}}>
                  <Button
                    style={styles.redoButtonFullScreen}
                    onPress={handleClearFullScreen}
                    size="sm"
                    labelStyle={{ color: CareshipColors.secondary }}
                    icon={<Feather color={CareshipColors.secondary} size={16} name="refresh-cw" />}
                  >
                    <FormattedMessage id="BUTTON_UNDO_SIGNATURE_SCREEN" />
                  </Button>
                  <Button
                    onPress={handleDone} // Close the modal
                    style={styles.doneButton}
                    size="sm"
                    icon={<Feather color='white' size={16} name="skip-forward" />}
                    labelStyle={{ color: 'white' }}
                    iconRight={true}
                  >
                    <FormattedMessage id="BUTTON_NEXT_SIGNATURE_SCREEN" />
                  </Button>
                </Container>

                <Container style={{height: '100%', width: '80%', marginBottom: 5, borderWidth: 1, borderColor: 'rgba(0,0,0,0.15)', marginLeft: 0}}>


                  <WebView
                    ref={webviewRef2}
                    originWhitelist={['*']}
                    source={{ html: signaturePadHtmlFullScreen }}
                    javaScriptEnabled={true}
                    onMessage={handleMessageRotated}
                    style={[styles.webview, { width: '100%', height: '100%' }]}
                  />
                </Container>

              </View>
            </View>
          </View>
        </Modal>
      </Portal>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    maxHeight: 250,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  webview: {
    borderColor: 'black',
    borderWidth: 0,
    width: '100%',
    marginBottom:5
  },
  buttons: {
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: 0,
    width: 'auto',
    height:25
  },
  buttonsSpecial: {
    marginTop: 0,
    width: 0,
    height:0,
    padding:0,
    marginLeft: '100%',
    marginRight: '0%',
    zIndex: 10
  },
  buttonsFullScreen: {
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    width: '20%',
  },
  redoButton: {
    backgroundColor: 'white',
    borderStyle: 'solid',
    borderColor: CareshipColors.secondary,
    borderWidth: 1,
  },
  fullScreenButton: {
    backgroundColor: 'white',
    borderStyle: 'solid',
    borderColor: CareshipColors.secondary,
    borderWidth: 1,
  },
  doneButton: {
    backgroundColor: CareshipColors.primary,
    borderStyle: 'solid',
    borderColor: CareshipColors.primary,
    borderWidth: 1,
    height: 50,
    width: 130,
    marginLeft:-25,
    marginTop: 150,
    transform: [
      {rotate: '90deg'}
    ]
  },
  redoButtonFullScreen: {
    backgroundColor: 'white',
    borderStyle: 'solid',
    borderColor: CareshipColors.secondary,
    borderWidth: 1,
    height: 50,
    width: 150,
    marginLeft: -35,
    paddingLeft: 10,
    paddingRight: 10,
    transform: [
      {rotate: '90deg'},
    ],
  },
  modalContainer: {
    position: "absolute",
    width: '100%',
    height: '98%',
    left: 0,
    top: 0,
    backgroundColor: 'white'
  },
  containerHolder: {
    height: '100%',
    paddingTop: 60,
    paddingBottom: 60
  },
  modalContent: {
    width: '100%',
    fontSize: 12,
    height: '90%',
  },
  modal: {
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 30,
    borderRadius: 10,
    alignSelf: 'center'
  },
  group: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: 12
  },
  groupFullScreen: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: 12,
    flexDirection: "row"
  },
  specialButtonsNormal:{
    padding:0,
    zIndex:3,
    backgroundColor: 'black',
    width: 10,
    height: 32,
    paddingLeft:0,
    paddingRight: 0,
    marginTop: -16,
    minWidth: 32,
  }
});

export default SignatureScreen;
